import {
  OpsCeilingPartKind,
  OpsInfrastructureSourceState,
} from '../../../../../libs/api-client/src/generated/models';
import { useLocale } from '../../i18n/LocaleProvider';
import type { OpsDatabase } from '../../lib/infrastructure-api';
import {
  formatDuration,
  formatNumber,
  formatPercent,
  LEVEL_COLOURS,
  meterWidth,
  shortLabel,
} from '../../lib/infrastructure';
import {
  LevelBadge,
  Meter,
  RingGauge,
  Section,
  Sparkline,
  Stat,
} from './InfrastructureParts';

/** The label the API uses for a Unix-socket connection. */
const LOCAL_SOURCE = 'local';

/**
 * The Postgres connection budget (work order AC3/AC4): what is open now, against what the database
 * can take — and, the number that matters before a scale-out, the SUM of every configured pool
 * against that limit. The API decides every level; this only draws them.
 */
export function DatabasePanel({
  database,
  state,
  nodePrefix,
}: {
  readonly database: OpsDatabase | null;
  readonly state: OpsInfrastructureSourceState;
  /** The shared node-name prefix, cut from labels so the table does not truncate them. */
  readonly nodePrefix: string;
}) {
  const { t, locale } = useLocale();

  if (!database) {
    return (
      <Section
        title={t('infrastructure.database.title')}
        className="lg:col-span-2"
      >
        <p className="text-sm text-gray-600">
          {state === OpsInfrastructureSourceState.NotConfigured
            ? t('infrastructure.database.notConfigured')
            : t('infrastructure.database.error')}
        </p>
      </Section>
    );
  }

  const states: [string, number][] = [
    [t('infrastructure.database.active'), database.active],
    [t('infrastructure.database.idle'), database.idle],
    [
      t('infrastructure.database.idleInTransaction'),
      database.idleInTransaction,
    ],
    [t('infrastructure.database.other'), database.other],
  ];
  const sourceLabel = (label: string): string =>
    label === LOCAL_SOURCE
      ? t('infrastructure.database.local')
      : shortLabel(label, nodePrefix);

  return (
    <Section
      title={t('infrastructure.database.title')}
      aside={<LevelBadge level={database.openLevel} />}
      className="lg:col-span-2"
    >
      {/* Now vs. the limit, and the sum of what could be opened vs. the limit. */}
      <div className="grid gap-6 sm:grid-cols-[minmax(0,13rem)_1fr]">
        <div className="flex flex-col items-center gap-4">
          <RingGauge
            label={t('infrastructure.database.open')}
            percent={database.openPercent}
            display={`${formatNumber(database.open, locale)}/${formatNumber(database.usable, locale)}`}
            level={database.openLevel}
            detail={t('infrastructure.database.usable', {
              usable: formatNumber(database.usable, locale),
              max: formatNumber(database.maxConnections, locale),
              reserved: formatNumber(
                database.superuserReserved + database.reserved,
                locale,
              ),
            })}
          />
          <dl className="grid w-full grid-cols-2 gap-x-3 gap-y-2">
            {states.map(([label, value]) => (
              <Stat
                key={label}
                label={label}
                value={formatNumber(value, locale)}
              />
            ))}
          </dl>
        </div>

        <div className="min-w-0 rounded-lg border border-gray-100 bg-gray-50/60 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('infrastructure.database.ceilingTitle')}
            </h3>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">
              {t('infrastructure.database.ceilingValue', {
                ceiling: formatNumber(database.ceiling, locale),
                usable: formatNumber(database.usable, locale),
              })}{' '}
              <span className="font-normal text-gray-500">
                ({formatPercent(database.ceilingPercent, locale)})
              </span>
            </span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1">
              <Meter
                percent={database.ceilingPercent}
                level={database.ceilingLevel}
                label={t('infrastructure.database.ceilingTitle')}
              />
            </div>
            <LevelBadge level={database.ceilingLevel} />
          </div>
          <p className="mt-2 text-xs leading-relaxed text-gray-500">
            {t('infrastructure.database.ceilingLead')}
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {database.ceilingParts.map((part) => (
              <li
                key={`${part.kind}-${part.label}`}
                className="rounded-md bg-white px-2 py-0.5 text-xs text-gray-600 ring-1 ring-inset ring-gray-200"
                title={part.label}
              >
                {part.kind === OpsCeilingPartKind.Monitoring
                  ? t('infrastructure.database.ceilingKind.monitoring')
                  : sourceLabel(part.label)}{' '}
                <span className="font-semibold text-gray-900 tabular-nums">
                  {formatNumber(part.connections, locale)}
                </span>
                {part.kind === OpsCeilingPartKind.Observed &&
                  ` · ${t('infrastructure.database.ceilingKind.observed')}`}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Where the connections come from. */}
      <div className="min-w-0">
        <h3 className="mb-2 text-sm font-semibold text-gray-900">
          {t('infrastructure.database.sources')}
        </h3>
        <div className="overflow-x-auto">
          <table className="data-table min-w-[26rem]">
            <thead>
              <tr>
                <th className="py-2 pr-3">
                  {t('infrastructure.database.sourceColumns.source')}
                </th>
                <th className="py-2 pr-3 text-right">
                  {t('infrastructure.database.sourceColumns.open')}
                </th>
                <th className="py-2 pr-3 text-right">
                  {t('infrastructure.database.sourceColumns.active')}
                </th>
                <th className="py-2 pr-3 text-right">
                  {t('infrastructure.database.sourceColumns.idleInTx')}
                </th>
                <th className="py-2 pr-3 text-right">
                  {t('infrastructure.database.sourceColumns.cap')}
                </th>
                <th className="w-1/4 py-2" aria-hidden="true" />
              </tr>
            </thead>
            <tbody className="tabular-nums text-gray-700">
              {database.sources.map((source) => {
                const share =
                  source.cap !== null && source.cap > 0
                    ? (source.open / source.cap) * 100
                    : null;
                return (
                  <tr key={source.label}>
                    <td
                      className="py-2 pr-3 font-medium text-gray-900"
                      title={source.label}
                    >
                      {sourceLabel(source.label)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {formatNumber(source.open, locale)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {formatNumber(source.active, locale)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {formatNumber(source.idleInTransaction, locale)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {formatNumber(source.cap, locale)}
                    </td>
                    <td className="py-2" aria-hidden="true">
                      {share !== null && (
                        <div
                          className="h-1.5 w-full overflow-hidden rounded-full"
                          style={{ background: LEVEL_COLOURS.ok.track }}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: meterWidth(share),
                              background: LEVEL_COLOURS.ok.mark,
                            }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!database.sourcesMapped && (
          <p className="mt-2 text-xs text-gray-500">
            {t('infrastructure.database.unmapped')}
          </p>
        )}
      </div>

      {/* What is slow right now, and the last hour. */}
      <div className="grid gap-4 border-t border-gray-100 pt-4 sm:grid-cols-2">
        <dl className="grid grid-cols-3 gap-3">
          <Stat
            label={t('infrastructure.database.longestActive')}
            value={formatDuration(database.longestActiveSeconds)}
          />
          <Stat
            label={t('infrastructure.database.longestIdleInTx')}
            value={formatDuration(database.longestIdleInTransactionSeconds)}
            sub={<LevelBadge level={database.idleInTransactionLevel} />}
          />
          <Stat
            label={t('infrastructure.database.lockWaits')}
            value={formatNumber(database.lockWaits, locale)}
          />
        </dl>
        <Sparkline
          title={t('infrastructure.database.history')}
          points={database.history.map((p) => ({ t: p.t, value: p.open }))}
          format={(v) => formatNumber(v, locale)}
        />
      </div>
    </Section>
  );
}
